import { addMonths, addQuarters, addWeeks, isBefore, isSameQuarter } from "date-fns"
import { UTCDateMini } from "@date-fns/utc"
import {
  convertToEndOfWeekUTC,
  convertToStartOfWeekUTC,
  convertToStartOfYearUTC,
  DateFormatType,
  formatDate,
  getStartOfYearUTC,
  isSameMonth,
  isSameWeek,
} from "~/utils/datetime"
import {
  dateToFiscal,
  fiscalPeriodMonthStart,
  fiscalPeriodRange,
  fiscalYearFromLabel,
  fiscalYearStartDate,
  formatFiscalWeekLabel,
  type FiscalConfig,
} from "~/utils/fiscalCalendar"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"

export type CalendarViewOption = "weekly" | "monthly" | "quarterly"

export function generateCalendarDatesForYear(
  year: number,
  viewOption: CalendarViewOption,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): Date[] {
  // Weekly view on a fiscal calendar: span the FISCAL year (anchor → next anchor) so the year
  // switcher's first column is fiscal week 1, not the ISO week containing Jan 1.
  if (viewOption === "weekly" && fiscalConfig) {
    const fiscalYear = fiscalYearFromLabel(year, fiscalConfig)
    const firstDate = fiscalYearStartDate(fiscalYear, fiscalConfig)
    const lastDate = fiscalYearStartDate(fiscalYear + 1, fiscalConfig)
    const allDates: Date[] = []
    let currentDate = firstDate
    while (isBefore(currentDate, lastDate)) {
      allDates.push(currentDate)
      currentDate = addWeeks(currentDate, 1)
    }
    return allDates
  }

  // Monthly view on a fiscal calendar: the 12 fiscal periods of the fiscal year (4-5-4 / 4-4-5 /
  // 5-4-4), each represented by its first Monday.
  if (viewOption === "monthly" && fiscalConfig) {
    return Array.from(
      { length: 12 },
      (_, period) =>
        fiscalPeriodRange(fiscalYearFromLabel(year, fiscalConfig), period, fiscalConfig).start,
    )
  }

  const yearStart = convertToStartOfYearUTC(new UTCDateMini(`${year}-01-01`))
  const firstDate = viewOption === "weekly" ? convertToStartOfWeekUTC(yearStart) : yearStart
  const lastDate = convertToStartOfYearUTC(new UTCDateMini(`${year + 1}-01-01`))

  let currentDate = firstDate
  const allDates: Date[] = []

  while (isBefore(currentDate, lastDate)) {
    allDates.push(currentDate)
    switch (viewOption) {
      case "weekly":
        currentDate = addWeeks(currentDate, 1)
        break
      case "monthly":
        currentDate = addMonths(currentDate, 1)
        break
      case "quarterly":
        currentDate = addQuarters(currentDate, 1)
        break
    }
  }

  return allDates
}

export function getNext12Months(): { key: string; label: string }[] {
  const months = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("default", { month: "short", year: "numeric" }),
    })
  }
  return months
}

/**
 * `[start, end)` date range covered by a year in the given view: the fiscal year labelled `year` (anchor → next
 * anchor) for the weekly view on a fiscal calendar, otherwise the calendar year. Keep data fetches
 * and date filters aligned to this so every displayed column is populated (REKD-646).
 */
export function calendarYearRange(
  year: number,
  viewOption: CalendarViewOption,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): { start: Date; end: Date } {
  // Weekly and monthly fiscal views both span the fiscal year (anchor → next anchor).
  if ((viewOption === "weekly" || viewOption === "monthly") && fiscalConfig) {
    const fiscalYear = fiscalYearFromLabel(year, fiscalConfig)
    return {
      start: fiscalYearStartDate(fiscalYear, fiscalConfig),
      end: fiscalYearStartDate(fiscalYear + 1, fiscalConfig),
    }
  }
  return { start: getStartOfYearUTC(year), end: getStartOfYearUTC(year + 1) }
}

export function formatCalendarHeader(
  date: Date,
  viewOption: CalendarViewOption,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): string {
  switch (viewOption) {
    case "weekly":
      return (
        "W" +
        (fiscalConfig
          ? formatFiscalWeekLabel(date, fiscalConfig, DateFormatType.WeekYear)
          : formatDate(date, DateFormatType.WeekYear))
      )
    case "monthly":
      if (fiscalConfig) {
        // `date` is a fiscal period's first Monday (may sit in the prior calendar month); label by
        // the calendar month the period represents.
        const { fiscalYear, fiscalPeriod } = dateToFiscal(date, fiscalConfig)
        return formatDate(
          fiscalPeriodMonthStart(fiscalYear, fiscalPeriod, fiscalConfig),
          DateFormatType.MonthYear,
        )
      }
      return formatDate(date, DateFormatType.MonthYear)
    case "quarterly":
      return formatDate(date, DateFormatType.QuarterYear)
  }
}

/**
 * The calendar date range a column covers, shown beneath the (possibly fiscal) label so the real
 * dates are always visible: Mon→Sun for a week, or the fiscal period's first-Monday→last-Sunday for
 * a fiscal month. `undefined` for plain calendar months/quarters, whose headers are already dates.
 */
export function formatDayMonthRange(start: Date, end: Date): string {
  return `${formatDate(start, DateFormatType.DayMonth)} → ${formatDate(end, DateFormatType.DayMonth)}`
}

/**
 * The Mon→Sun calendar dates of the week containing `date`, e.g. "29 Dec → 04 Jan". Fiscal week
 * numbering is label-only and never moves week boundaries, so this is fiscal-independent — show it
 * next to a (possibly fiscal) week label so the real dates are always visible.
 */
export function formatWeekRangeLabel(date: Date): string {
  return formatDayMonthRange(convertToStartOfWeekUTC(date), convertToEndOfWeekUTC(date))
}

export function formatCalendarSubHeader(
  date: Date,
  viewOption: CalendarViewOption,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): string | undefined {
  if (viewOption === "weekly") {
    return formatWeekRangeLabel(date)
  }
  if (viewOption === "monthly" && fiscalConfig) {
    const { fiscalYear, fiscalPeriod } = dateToFiscal(date, fiscalConfig)
    const { start, end } = fiscalPeriodRange(fiscalYear, fiscalPeriod, fiscalConfig)
    return formatDayMonthRange(start, end)
  }
  return undefined
}

/**
 * Whether `checkDate` falls in the same calendar column as `columnDate` for the given view. Weekly
 * uses ISO weeks; quarterly uses calendar quarters; monthly uses the fiscal period when a fiscal
 * calendar is set, otherwise the calendar month. Replaces per-page isSame{Week,Month,Quarter}.
 */
export function isSameCalendarPeriod(
  checkDate: Date,
  columnDate: Date,
  viewOption: CalendarViewOption,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): boolean {
  switch (viewOption) {
    case "weekly":
      return isSameWeek(checkDate, columnDate)
    case "monthly":
      if (fiscalConfig) {
        const a = dateToFiscal(checkDate, fiscalConfig)
        const b = dateToFiscal(columnDate, fiscalConfig)
        return a.fiscalYear === b.fiscalYear && a.fiscalPeriod === b.fiscalPeriod
      }
      return isSameMonth(checkDate, columnDate)
    case "quarterly":
      return isSameQuarter(checkDate, columnDate)
  }
}

/**
 * Exclusive end of the calendar column starting at `columnDate` — for building `[start, end)` data
 * windows. Monthly on a fiscal calendar returns the fiscal period's exclusive end (next period's
 * first Monday); otherwise it adds one week/month/quarter.
 */
export function calendarColumnEnd(
  columnDate: Date,
  viewOption: CalendarViewOption,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): Date {
  switch (viewOption) {
    case "weekly":
      return addWeeks(columnDate, 1)
    case "monthly":
      if (fiscalConfig) {
        const { fiscalYear, fiscalPeriod } = dateToFiscal(columnDate, fiscalConfig)
        return new Date(fiscalPeriodRange(fiscalYear, fiscalPeriod, fiscalConfig).end.getTime() + 1)
      }
      return addMonths(columnDate, 1)
    case "quarterly":
      return addQuarters(columnDate, 1)
  }
}
